import React from 'react';
import { Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Body1, Header1 } from '../blocks';
import CarrierBadges from './CarrierBadges';

// The primary card is green, with PRIMARY set into a gap in its top border (a fieldset legend, so the
// browser cuts the gap and the tint behind it needs no matching background). `onMakePrimary` is
// passed for the subregion's other carriers; the primary card has no "+".
// `onHoverChange(true|false)` fires as the pointer or keyboard focus enters and leaves the card, so the
// map can overlay the carrier's regions; `noRegions` says it has none to show.
const CarrierCard = ({
    name,
    statuses,
    primary = false,
    onMakePrimary,
    onRemove,
    onDetails,
    onHoverChange,
    noRegions = false,
    disabled,
}) => (
    <fieldset
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        onFocus={() => onHoverChange?.(true)}
        // Moving focus between the card's own controls isn't leaving it.
        onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) onHoverChange?.(false);
        }}
        css={css`
            margin: 0;
            min-width: 0;
            padding: 10px 12px;
            border: 1px solid ${primary ? colors.greens.primary : colors.greys.border};
            border-radius: 4px;
            background: ${primary ? `${colors.greens.primary}0D` : 'transparent'};
        `}
    >
        {primary ? (
            <legend
                css={css`
                    /* Bootstrap's reset floats a legend at full width, which pulls it out of the border and
                       gaps the whole top edge. */
                    float: none;
                    width: auto;
                    max-width: none;
                    margin: 0 auto;
                    padding: 0 6px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    line-height: 1;
                    color: ${colors.greens.primary};
                `}
            >
                PRIMARY
            </legend>
        ) : null}
        <Grid
            container
            direction="column"
            css={css`
                flex-wrap: nowrap;
                gap: 6px;
            `}
        >
            <Grid
                container
                css={css`
                    flex-wrap: nowrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                `}
            >
                <Header1
                    css={css`
                        flex: 1;
                        font-weight: 600;
                        color: ${colors.greys[3]};
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    `}
                >
                    {name}
                </Header1>
                {onMakePrimary ? (
                    <Tooltip title="Make primary">
                        {/* A disabled button fires no events, so the tooltip hangs off the span. */}
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`make ${name} primary`}
                                onClick={onMakePrimary}
                                disabled={disabled}
                                css={css`
                                    color: ${colors.greens.primary};
                                `}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
                <Tooltip title="Remove">
                    <span>
                        <IconButton
                            size="small"
                            aria-label={`remove ${name}`}
                            onClick={onRemove}
                            disabled={disabled}
                            css={css`
                                color: ${colors.reds.primary};
                            `}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid>
            <CarrierBadges statuses={statuses} />
            {noRegions ? (
                <Body1
                    css={css`
                        color: ${colors.golds.primary};
                    `}
                >
                    No regions configured
                </Body1>
            ) : null}
            <Tooltip title="Set up Middle Mile & WH Auto Assign Carriers">
                <Button
                    size="small"
                    onClick={onDetails}
                    css={css`
                        align-self: flex-start;
                        padding: 0 4px;
                        min-width: 0;
                        text-transform: none;
                        color: ${colors.greys[2]};
                        text-decoration: underline;
                    `}
                >
                    More details
                </Button>
            </Tooltip>
        </Grid>
    </fieldset>
);

export default CarrierCard;
